import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'admin_token';

export const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // If already logged in, redirect to admin
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      navigate('/admin');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      localStorage.setItem(TOKEN_KEY, response.data.access_token);
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login fehlgeschlagen');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <div className="admin-logo">NH</div>
            <h1>Admin Bereich</h1>
            <p>Bitte melde dich an, um fortzufahren.</p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login-form">
            <div className="admin-form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                placeholder="Username"
              />
            </div>
            <div className="admin-form-group">
              <label>Passwort</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            {error && <div className="admin-error">{error}</div>}

            <button type="submit" className="admin-login-button" disabled={loading}>
              {loading ? 'Anmeldung läuft...' : 'Anmelden →'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [ebookLeads, setEbookLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Password change state
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  // Search state per tab
  const [inquirySearch, setInquirySearch] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [ebookSearch, setEbookSearch] = useState('');

  const token = localStorage.getItem(TOKEN_KEY);

  const apiHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, analyticsRes, inquiriesRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, apiHeaders),
        axios.get(`${API}/admin/bookings`, apiHeaders),
        axios.get(`${API}/admin/analytics`, apiHeaders),
        axios.get(`${API}/admin/contact-inquiries`, apiHeaders),
        axios.get(`${API}/admin/ebook-leads`, apiHeaders),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setAnalytics(analyticsRes.data);
      setInquiries(inquiriesRes.data);
      setEbookLeads(leadsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate('/admin/login');
      }
      console.error('Error loading admin data:', err);
    }
    setLoading(false);
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contact-inquiries/${id}/status?status=${status}`, {}, apiHeaders);
      loadData();
    } catch (err) {
      console.error('Error updating inquiry status:', err);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Diese Anfrage wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/contact-inquiries/${id}`, apiHeaders);
      loadData();
    } catch (err) {
      console.error('Error deleting inquiry:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate('/admin/login');
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/status?status=${status}`,
        {},
        apiHeaders
      );
      loadData();
    } catch (err) {
      console.error('Error updating booking:', err);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Diese Buchung wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, apiHeaders);
      loadData();
    } catch (err) {
      console.error('Error deleting booking:', err);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (pwNew.length < 8) {
      setPwError('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('Die beiden neuen Passwörter stimmen nicht überein.');
      return;
    }
    if (pwCurrent === pwNew) {
      setPwError('Das neue Passwort muss sich vom aktuellen unterscheiden.');
      return;
    }

    setPwLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        apiHeaders
      );
      setPwSuccess('Passwort erfolgreich geändert. Beim nächsten Login bitte das neue Passwort verwenden.');
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPwError(typeof detail === 'string' ? detail : 'Passwortänderung fehlgeschlagen.');
    }
    setPwLoading(false);
  };


  const exportCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const keys = Object.keys(data[0]);
    const csvHeader = keys.join(',');
    const csvRows = data.map(row => 
      keys.map(k => {
        const value = row[k] ?? '';
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusBadge = (status) => {
    const colors = {
      pending: { bg: '#f59e0b', text: '#000' },
      confirmed: { bg: '#10b981', text: '#fff' },
      cancelled: { bg: '#ef4444', text: '#fff' },
      completed: { bg: '#6366f1', text: '#fff' },
      paid: { bg: '#10b981', text: '#fff' },
      initiated: { bg: '#94a3b8', text: '#000' }
    };
    const color = colors[status] || colors.pending;
    return (
      <span 
        className="status-badge"
        style={{ background: color.bg, color: color.text }}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loader"></div>
        <p>Lade Admin-Daten...</p>
      </div>
    );
  }

  // Filter helpers
  const matches = (value, query) =>
    String(value || '').toLowerCase().includes(query.toLowerCase());

  const filteredInquiries = inquiries.filter((inq) => {
    const q = inquirySearch.trim();
    if (!q) return true;
    return (
      matches(inq.confirmation_number, q) ||
      matches(inq.name, q) ||
      matches(inq.email, q) ||
      matches(inq.phone, q) ||
      matches(inq.package, q) ||
      matches(inq.message, q) ||
      matches(inq.status, q)
    );
  });

  const filteredBookings = bookings.filter((b) => {
    const q = bookingSearch.trim();
    if (!q) return true;
    return (
      matches(b.name, q) ||
      matches(b.email, q) ||
      matches(b.phone, q) ||
      matches(b.package, q) ||
      matches(b.message, q) ||
      matches(b.date, q) ||
      matches(b.time, q) ||
      matches(b.status, q)
    );
  });

  const filteredEbookLeads = ebookLeads.filter((l) => {
    const q = ebookSearch.trim();
    if (!q) return true;
    return matches(l.email, q) || matches(l.name, q);
  });

  const searchInputStyle = {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: 10,
    fontSize: 14,
    width: 320,
    maxWidth: '100%',
    outline: 'none',
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <div className="admin-logo small">NH</div>
            <div>
              <h1>Admin Dashboard</h1>
              <p>SocialAgency@nielshoefner.com</p>
            </div>
          </div>
          <div className="admin-header-actions">
            <button onClick={loadData} className="admin-btn admin-btn-secondary">
              🔄 Aktualisieren
            </button>
            <button onClick={handleLogout} className="admin-btn admin-btn-outline">
              Abmelden →
            </button>
          </div>
        </div>
      </header>

      <nav className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Übersicht
        </button>
        <button 
          className={`admin-tab ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          📅 Buchungen ({bookings.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'inquiries' ? 'active' : ''}`}
          onClick={() => setActiveTab('inquiries')}
          data-testid="admin-tab-inquiries"
        >
          📨 Anfragen ({inquiries.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'ebook' ? 'active' : ''}`}
          onClick={() => setActiveTab('ebook')}
          data-testid="admin-tab-ebook"
        >
          📚 E-Book Leads ({ebookLeads.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
          data-testid="admin-tab-analytics"
        >
          📈 Besucher
        </button>
        <button
          className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
          data-testid="admin-tab-settings"
        >
          ⚙️ Einstellungen
        </button>
      </nav>

      <main className="admin-content">
        {/* Overview */}
        {activeTab === 'overview' && stats && (
          <div className="admin-section">
            <h2 className="admin-section-title">Statistiken & KPIs</h2>
            
            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon">📅</div>
                <div className="stat-value">{stats.total_bookings}</div>
                <div className="stat-label">Gesamt Buchungen</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-icon">⏰</div>
                <div className="stat-value">{stats.today_bookings}</div>
                <div className="stat-label">Buchungen heute</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon">⏳</div>
                <div className="stat-value">{stats.pending_bookings}</div>
                <div className="stat-label">Offene Buchungen</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-value">{stats.confirmed_bookings || 0}</div>
                <div className="stat-label">Bestätigte Buchungen</div>
              </div>
              {analytics && (
                <div className="stat-card highlight">
                  <div className="stat-icon">👁️</div>
                  <div className="stat-value">{analytics.today_views}</div>
                  <div className="stat-label">Besucher heute</div>
                </div>
              )}
            </div>

            <div className="quick-actions">
              <h3>Schnellaktionen</h3>
              <div className="action-buttons">
                <button 
                  className="admin-btn admin-btn-primary"
                  onClick={() => setActiveTab('bookings')}
                >
                  📅 Buchungen verwalten
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bookings */}
        {activeTab === 'bookings' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2 className="admin-section-title">Termin-Buchungen</h2>
              <button 
                className="admin-btn admin-btn-outline"
                onClick={() => exportCSV(filteredBookings, 'buchungen')}
                disabled={filteredBookings.length === 0}
              >
                📥 CSV exportieren
              </button>
            </div>

            {/* Search */}
            {bookings.length > 0 && (
              <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={bookingSearch}
                  onChange={(e) => setBookingSearch(e.target.value)}
                  placeholder="🔍 Suche nach Name, E-Mail, Datum, Status..."
                  style={{ ...searchInputStyle, width: 420 }}
                  data-testid="booking-search-input"
                />
                {bookingSearch && (
                  <>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {filteredBookings.length} von {bookings.length}
                    </span>
                    <button
                      onClick={() => setBookingSearch('')}
                      className="admin-btn admin-btn-outline"
                      style={{ padding: '6px 14px', fontSize: 13 }}
                    >
                      ✕ Zurücksetzen
                    </button>
                  </>
                )}
              </div>
            )}

            {bookings.length === 0 ? (
              <div className="admin-empty">
                <p>Noch keine Buchungen vorhanden.</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="admin-empty">
                <p>Keine Treffer für "{bookingSearch}".</p>
              </div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Termin</th>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Paket</th>
                      <th>Nachricht</th>
                      <th>Status</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id}>
                        <td>
                          <strong>{booking.date}</strong><br />
                          <small>{booking.time} Uhr</small>
                        </td>
                        <td>{booking.name}</td>
                        <td>
                          <a href={`mailto:${booking.email}`}>{booking.email}</a><br />
                          <small>
                            <a href={`https://wa.me/49${booking.phone.replace(/[\s\-+]|^0/g, '')}`} target="_blank" rel="noopener noreferrer">
                              {booking.phone}
                            </a>
                          </small>
                        </td>
                        <td>{booking.package || '-'}</td>
                        <td className="message-cell">
                          {booking.message || '-'}
                        </td>
                        <td>{statusBadge(booking.status)}</td>
                        <td>
                          <div className="action-cell">
                            <select
                              value={booking.status}
                              onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                              className="status-select"
                            >
                              <option value="pending">Offen</option>
                              <option value="confirmed">Bestätigt</option>
                              <option value="completed">Erledigt</option>
                              <option value="cancelled">Abgesagt</option>
                            </select>
                            <button 
                              className="btn-delete"
                              onClick={() => deleteBooking(booking.id)}
                              title="Löschen"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Contact Inquiries */}
        {activeTab === 'inquiries' && (
          <div className="admin-section" data-testid="admin-inquiries-section">
            <div className="admin-section-header">
              <h2 className="admin-section-title">📨 Kontakt-Anfragen</h2>
              <button
                className="admin-btn admin-btn-outline"
                onClick={() => exportCSV(filteredInquiries, 'anfragen')}
                disabled={filteredInquiries.length === 0}
              >
                📥 CSV exportieren
              </button>
            </div>

            {/* Search */}
            {inquiries.length > 0 && (
              <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={inquirySearch}
                  onChange={(e) => setInquirySearch(e.target.value)}
                  placeholder="🔍 Suche nach Bestätigungsnr., Name, E-Mail, Telefon, Nachricht..."
                  style={{ ...searchInputStyle, width: 460 }}
                  data-testid="inquiry-search-input"
                />
                {inquirySearch && (
                  <>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {filteredInquiries.length} von {inquiries.length}
                    </span>
                    <button
                      onClick={() => setInquirySearch('')}
                      className="admin-btn admin-btn-outline"
                      style={{ padding: '6px 14px', fontSize: 13 }}
                    >
                      ✕ Zurücksetzen
                    </button>
                  </>
                )}
              </div>
            )}

            {inquiries.length === 0 ? (
              <div className="admin-empty">
                <p>Noch keine Anfragen über das Kontaktformular eingegangen.</p>
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="admin-empty">
                <p>Keine Treffer für "{inquirySearch}".</p>
              </div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Bestätigungsnr.</th>
                      <th>Eingegangen</th>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Interesse</th>
                      <th>Nachricht</th>
                      <th>Status</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInquiries.map((inq) => (
                      <tr key={inq.id}>
                        <td>
                          <code style={{ color: '#D4AF37', fontSize: 12, fontWeight: 600 }}>
                            {inq.confirmation_number || '—'}
                          </code>
                        </td>
                        <td>{formatDate(inq.created_at)}</td>
                        <td><strong>{inq.name}</strong></td>
                        <td>
                          <a href={`mailto:${inq.email}`}>{inq.email}</a>
                          {inq.phone && (
                            <>
                              <br />
                              <small>
                                <a
                                  href={`https://wa.me/49${(inq.phone || '').replace(/[\s\-+]|^0/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {inq.phone}
                                </a>
                              </small>
                            </>
                          )}
                        </td>
                        <td>{inq.package || '-'}</td>
                        <td className="message-cell">{inq.message}</td>
                        <td>{statusBadge(inq.status)}</td>
                        <td>
                          <div className="action-cell">
                            <select
                              value={inq.status}
                              onChange={(e) => updateInquiryStatus(inq.id, e.target.value)}
                              className="status-select"
                            >
                              <option value="new">Neu</option>
                              <option value="contacted">Kontaktiert</option>
                              <option value="done">Erledigt</option>
                              <option value="archived">Archiviert</option>
                            </select>
                            <button
                              className="btn-delete"
                              onClick={() => deleteInquiry(inq.id)}
                              title="Löschen"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* E-Book Leads */}
        {activeTab === 'ebook' && (
          <div className="admin-section" data-testid="admin-ebook-section">
            <div className="admin-section-header">
              <h2 className="admin-section-title">📚 E-Book Leads</h2>
              <button
                className="admin-btn admin-btn-outline"
                onClick={() => exportCSV(filteredEbookLeads, 'ebook_leads')}
                disabled={filteredEbookLeads.length === 0}
              >
                📥 CSV exportieren
              </button>
            </div>

            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: -8, marginBottom: 16, fontSize: 14 }}>
              Alle E-Mail-Adressen, die das gratis E-Book heruntergeladen haben.
            </p>

            {/* Search */}
            {ebookLeads.length > 0 && (
              <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={ebookSearch}
                  onChange={(e) => setEbookSearch(e.target.value)}
                  placeholder="🔍 Suche nach Name oder E-Mail..."
                  style={searchInputStyle}
                  data-testid="ebook-search-input"
                />
                {ebookSearch && (
                  <>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {filteredEbookLeads.length} von {ebookLeads.length}
                    </span>
                    <button
                      onClick={() => setEbookSearch('')}
                      className="admin-btn admin-btn-outline"
                      style={{ padding: '6px 14px', fontSize: 13 }}
                    >
                      ✕ Zurücksetzen
                    </button>
                  </>
                )}
              </div>
            )}

            {ebookLeads.length === 0 ? (
              <div className="admin-empty">
                <p>Noch keine E-Book Downloads.</p>
              </div>
            ) : filteredEbookLeads.length === 0 ? (
              <div className="admin-empty">
                <p>Keine Treffer für "{ebookSearch}".</p>
              </div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Erster Download</th>
                      <th>Name</th>
                      <th>E-Mail</th>
                      <th>Downloads</th>
                      <th>Letzter Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEbookLeads.map((lead) => (
                      <tr key={lead.id || lead.email}>
                        <td>{formatDate(lead.first_requested_at)}</td>
                        <td>{lead.name || '-'}</td>
                        <td>
                          <a href={`mailto:${lead.email}`}>{lead.email}</a>
                        </td>
                        <td><strong>{lead.download_count || 1}×</strong></td>
                        <td>{formatDate(lead.last_requested_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Analytics */}
        {activeTab === 'analytics' && analytics && (
          <div className="admin-section" data-testid="admin-analytics-section">
            <h2 className="admin-section-title">📈 Besucherzahlen</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: -8, marginBottom: 24, fontSize: 14 }}>
              DSGVO-konforme Statistik · keine Cookies · keine externen Tracker
            </p>

            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon">👁️</div>
                <div className="stat-value" data-testid="analytics-today-views">{analytics.today_views}</div>
                <div className="stat-label">Seitenaufrufe heute</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-icon">👤</div>
                <div className="stat-value" data-testid="analytics-today-unique">{analytics.today_unique}</div>
                <div className="stat-label">Besucher heute</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📅</div>
                <div className="stat-value">{analytics.views_7d}</div>
                <div className="stat-label">Aufrufe (7 Tage)</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{analytics.unique_7d}</div>
                <div className="stat-label">Besucher (7 Tage)</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📆</div>
                <div className="stat-value">{analytics.views_30d}</div>
                <div className="stat-label">Aufrufe (30 Tage)</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🌍</div>
                <div className="stat-value">{analytics.unique_30d}</div>
                <div className="stat-label">Besucher (30 Tage)</div>
              </div>
            </div>

            {/* Daily chart */}
            <div className="settings-card" style={{ maxWidth: '100%', marginTop: 32 }}>
              <h3 style={{ color: '#D4AF37', marginBottom: 16 }}>📊 Letzte 30 Tage</h3>
              {analytics.daily_chart.every(d => d.views === 0) ? (
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Noch keine Daten – sobald Besucher deine Seite öffnen, erscheinen sie hier.
                </p>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 160, overflowX: 'auto', paddingBottom: 24, position: 'relative' }}>
                  {(() => {
                    const maxV = Math.max(1, ...analytics.daily_chart.map(d => d.views));
                    return analytics.daily_chart.map((d) => {
                      const h = (d.views / maxV) * 140;
                      const label = d.day.slice(5); // MM-DD
                      return (
                        <div key={d.day} style={{ flex: '1 0 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 22 }} title={`${d.day}: ${d.views} Aufrufe`}>
                          <div style={{ width: '100%', height: Math.max(2, h), background: 'linear-gradient(180deg, #D4AF37 0%, #B8941F 100%)', borderRadius: '3px 3px 0 0' }} />
                          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginTop: 4, transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap' }}>{label}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Top pages & referrers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, marginTop: 32 }}>
              <div className="settings-card">
                <h3 style={{ color: '#D4AF37', marginBottom: 16 }}>🔝 Top Seiten (30 Tage)</h3>
                {analytics.top_paths.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.6)' }}>Noch keine Daten.</p>
                ) : (
                  <table className="admin-table" style={{ marginTop: 0 }}>
                    <thead>
                      <tr>
                        <th>Pfad</th>
                        <th style={{ textAlign: 'right' }}>Aufrufe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.top_paths.map((p) => (
                        <tr key={p.path}>
                          <td><code style={{ color: '#D4AF37' }}>{p.path}</code></td>
                          <td style={{ textAlign: 'right' }}><strong>{p.views}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="settings-card">
                <h3 style={{ color: '#D4AF37', marginBottom: 16 }}>🔗 Quellen (30 Tage)</h3>
                {analytics.top_referrers.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.6)' }}>Noch keine Daten.</p>
                ) : (
                  <table className="admin-table" style={{ marginTop: 0 }}>
                    <thead>
                      <tr>
                        <th>Quelle</th>
                        <th style={{ textAlign: 'right' }}>Aufrufe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.top_referrers.map((r) => (
                        <tr key={r.source}>
                          <td>{r.source}</td>
                          <td style={{ textAlign: 'right' }}><strong>{r.views}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <div className="admin-section" data-testid="admin-settings-section">
            <h2 className="admin-section-title">Einstellungen</h2>

            <div className="settings-card">
              <h3 style={{ color: '#D4AF37', marginBottom: 8 }}>🔒 Passwort ändern</h3>
              <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 20, fontSize: 14 }}>
                Wähle ein sicheres Passwort mit mindestens 8 Zeichen.
                Du wirst dich beim nächsten Login mit dem neuen Passwort anmelden.
              </p>

              <form onSubmit={handleChangePassword} className="admin-login-form" style={{ maxWidth: 480 }}>
                <div className="admin-form-group">
                  <label>Aktuelles Passwort</label>
                  <input
                    type="password"
                    value={pwCurrent}
                    onChange={(e) => setPwCurrent(e.target.value)}
                    required
                    placeholder="••••••••"
                    data-testid="pw-current-input"
                    autoComplete="current-password"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Neues Passwort</label>
                  <input
                    type="password"
                    value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Mindestens 8 Zeichen"
                    data-testid="pw-new-input"
                    autoComplete="new-password"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Neues Passwort wiederholen</label>
                  <input
                    type="password"
                    value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Neues Passwort erneut eingeben"
                    data-testid="pw-confirm-input"
                    autoComplete="new-password"
                  />
                </div>

                {pwError && (
                  <div className="admin-error" data-testid="pw-error">{pwError}</div>
                )}
                {pwSuccess && (
                  <div
                    className="admin-error"
                    style={{ background: 'rgba(16,185,129,0.15)', borderColor: '#10b981', color: '#10b981' }}
                    data-testid="pw-success"
                  >
                    ✓ {pwSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  className="admin-login-button"
                  disabled={pwLoading}
                  data-testid="pw-submit-button"
                >
                  {pwLoading ? 'Wird gespeichert...' : 'Passwort ändern →'}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
